import notification from "antd/es/notification";
import { pick } from "lodash-es";
import { call, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import SavedAddressType from "@mapmycustomers/shared/enum/SavedAddressType";
import { GeocodeResult, GeoStatus } from "@mapmycustomers/shared/types/base/Located";
import Organization from "@mapmycustomers/shared/types/Organization";
import SavedAddress, { CreateSavedAddressPayload } from "@mapmycustomers/shared/types/SavedAddress";
import { SimpleListResponse } from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";
import { isApiError } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import { callApi } from "@app/store/api/callApi";
import { login, logout } from "@app/store/auth/actions";
import { handleError } from "@app/store/errors/actions";
import { doesOrganizationSupportSavedLocations, getOrganizationId } from "@app/store/iam";
import {
  SavedLocationEvent,
  SavedLocationUpdatedField,
  trackSavedLocationEvent,
} from "@app/util/analytic/savedLocationsAnalytics";
import addressToSavedAddressFields from "@app/util/savedLocations/addressToSavedAddressFields";
import {
  hasSavedAddressErrorCode,
  RESERVED_SLOT_TAKEN_CODE,
  SAVED_ADDRESS_LIMIT_CODE,
} from "@app/util/savedLocations/savedAddressErrors";

import {
  createSavedAddress,
  deleteSavedAddress,
  fetchSavedAddresses,
  markSavedAddressUsed,
  resetSavedAddresses,
  updateSavedAddress,
} from "./actions";
import messages, { savedAddressErrorMessages } from "./messages";
import { getSavedAddressById, isSavedAddressLimitReached } from "./selectors";

const SAVED_ADDRESS_TYPES = Object.values(SavedAddressType);
const ADDRESS_NOT_FOUND_STATUSES: string[] = [
  GeoStatus.ADDRESS_AND_CITY_MISSING,
  GeoStatus.ZERO_RESULTS,
];

// Thrown inside the create saga so a local cap check takes the same path as the server's
const createLimitError = () => {
  const error = new ApiError("Saved locations limit reached", {
    code: SAVED_ADDRESS_LIMIT_CODE,
    message: "Saved locations limit reached",
    validationErrors: [{ code: SAVED_ADDRESS_LIMIT_CODE, field: "type", message: "" }],
  });
  error.status = 400;
  return error;
};

const isResponseLost = (error: unknown) =>
  !isApiError(error) || !error.status || [502, 503, 504].includes(error.status);

const isListStale = (error: unknown) =>
  isResponseLost(error) ||
  (isApiError(error) && error.status === 404) ||
  hasSavedAddressErrorCode(error, RESERVED_SLOT_TAKEN_CODE);

function* refetchIfStale(error: unknown) {
  if (isListStale(error)) {
    yield put(fetchSavedAddresses.request());
  }
}

type GeoPointResolution<T> = { resolvedPayload?: T; status?: string };

// Takes only the address block from the response: its top-level type names the geocoding provider
function* resolveGeoPoint<T extends Partial<CreateSavedAddressPayload>>(
  orgId: Organization["id"],
  payload: T
) {
  if (payload.geoPoint || (payload.address === undefined && payload.city === undefined)) {
    return { resolvedPayload: payload };
  }
  const { address, geoPoint, status }: GeocodeResult = yield callApi(
    "geocodeAddress",
    orgId,
    pick(payload, ["address", "city", "country", "postalCode", "region"])
  );
  return geoPoint
    ? { resolvedPayload: { ...payload, ...addressToSavedAddressFields(address), geoPoint } }
    : { status };
}

// A geocode over its limit or with no provider also comes back without a point
const warnGeocodeFailed = (status?: string) =>
  notification.warning({
    message:
      !status || ADDRESS_NOT_FOUND_STATUSES.includes(status)
        ? i18nService.formatMessage(
            messages.geocodeNotFound,
            "We couldn't find that address. Please check it and try again."
          )
        : i18nService.formatMessage(
            messages.geocodeUnavailable,
            "We can't locate that address right now. Please try again later."
          ),
  });

function* failGeocode(
  failure: typeof createSavedAddress.failure | typeof updateSavedAddress.failure,
  status?: string,
  failureCallback?: (error: unknown) => void
) {
  warnGeocodeFailed(status);
  yield put(failure(undefined));
  if (failureCallback) {
    yield call(failureCallback, undefined);
  }
}

export function* onFetchSavedAddresses() {
  try {
    const supported: boolean = yield select(doesOrganizationSupportSavedLocations);
    // The server hides the list from support staff signed in as the rep and refuses their writes
    if (!supported || localSettings.wasSkeletonKeyUsed()) {
      return;
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    // This endpoint returns every row at once; its limit is the rep's cap, not a page size
    const { data, limit }: SimpleListResponse<SavedAddress> = yield callApi(
      "fetchSavedAddresses",
      orgId
    );
    yield put(
      fetchSavedAddresses.success({
        limit,
        savedAddresses: data.filter(({ type }) => SAVED_ADDRESS_TYPES.includes(type)),
      })
    );
  } catch (error) {
    yield put(fetchSavedAddresses.failure(error));
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* onCreateSavedAddress({
  payload: { callback, failureCallback, hideLimitNotification, payload, source },
}: ReturnType<typeof createSavedAddress.request>) {
  try {
    const limitReached: boolean = yield select(isSavedAddressLimitReached);
    if (limitReached) {
      throw createLimitError();
    }
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { resolvedPayload, status }: GeoPointResolution<typeof payload> = yield call(
      resolveGeoPoint,
      orgId,
      payload
    );
    const geoPoint = resolvedPayload?.geoPoint;
    if (!resolvedPayload || !geoPoint) {
      yield call(failGeocode, createSavedAddress.failure, status, failureCallback);
      return;
    }
    const savedAddress: SavedAddress = yield callApi("createSavedAddress", orgId, {
      ...resolvedPayload,
      geoPoint,
    });
    yield put(createSavedAddress.success(savedAddress));
    trackSavedLocationEvent(SavedLocationEvent.CREATED, { kind: savedAddress.type, source });
    if (callback) {
      yield call(callback, savedAddress);
    }
  } catch (error) {
    const isLimitError = hasSavedAddressErrorCode(error, SAVED_ADDRESS_LIMIT_CODE);
    if (isLimitError) {
      trackSavedLocationEvent(SavedLocationEvent.CAP_REACHED, { source });
    }
    yield put(createSavedAddress.failure(error));
    yield put(
      handleError({
        displayNotification: !(isLimitError && hideLimitNotification),
        error,
        messageByErrorCodeMap: savedAddressErrorMessages,
      })
    );
    yield call(refetchIfStale, error);
    if (failureCallback) {
      yield call(failureCallback, error);
    }
  }
}

export function* onUpdateSavedAddress({
  payload: { callback, failureCallback, payload },
}: ReturnType<typeof updateSavedAddress.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { resolvedPayload, status }: GeoPointResolution<typeof payload> = yield call(
      resolveGeoPoint,
      orgId,
      payload
    );
    if (!resolvedPayload) {
      yield call(failGeocode, updateSavedAddress.failure, status, failureCallback);
      return;
    }
    const savedAddress: SavedAddress = yield callApi("updateSavedAddress", orgId, resolvedPayload);
    yield put(updateSavedAddress.success({ replaceId: payload.replaceId, savedAddress }));
    if (resolvedPayload.name !== undefined) {
      trackSavedLocationEvent(SavedLocationEvent.UPDATED, {
        field_changed: SavedLocationUpdatedField.NAME,
        kind: savedAddress.type,
      });
    }
    if (resolvedPayload.geoPoint) {
      trackSavedLocationEvent(SavedLocationEvent.UPDATED, {
        field_changed: SavedLocationUpdatedField.ADDRESS,
        kind: savedAddress.type,
      });
    }
    if (callback) {
      yield call(callback, savedAddress);
    }
  } catch (error) {
    yield put(updateSavedAddress.failure(error));
    yield put(handleError({ error, messageByErrorCodeMap: savedAddressErrorMessages }));
    yield call(refetchIfStale, error);
    if (failureCallback) {
      yield call(failureCallback, error);
    }
  }
}

export function* onDeleteSavedAddress({
  payload: { id, callback, failureCallback, surface },
}: ReturnType<typeof deleteSavedAddress.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const savedAddress: SavedAddress | undefined = yield select(getSavedAddressById, id);
    yield callApi("deleteSavedAddress", orgId, id);
    yield put(deleteSavedAddress.success(id));
    if (savedAddress && savedAddress.type !== SavedAddressType.CUSTOM) {
      trackSavedLocationEvent(SavedLocationEvent.CLEARED, { kind: savedAddress.type });
    } else if (surface) {
      trackSavedLocationEvent(SavedLocationEvent.DELETED, { surface });
    }
    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(deleteSavedAddress.failure(error));
    yield put(handleError({ error }));
    yield call(refetchIfStale, error);
    if (failureCallback) {
      yield call(failureCallback, error);
    }
  }
}

export function* onMarkSavedAddressUsed({
  payload: id,
}: ReturnType<typeof markSavedAddressUsed.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("markSavedAddressUsed", orgId, id);
    yield put(markSavedAddressUsed.success({ id, lastUsedAt: new Date().toISOString() }));
  } catch (error) {
    yield put(markSavedAddressUsed.failure(error));
    yield put(handleError({ displayNotification: false, error }));
    yield call(refetchIfStale, error);
  }
}

// A sign-in or sign-out cancels a load in flight, so its list can't reach the next session.
export function* onFetchSavedAddressesUntilReset() {
  yield race({ fetch: call(onFetchSavedAddresses), reset: take(resetSavedAddresses) });
}

export function* onSessionEnd() {
  yield put(resetSavedAddresses());
}

export function* savedLocationsSaga() {
  yield takeLatest(fetchSavedAddresses.request, onFetchSavedAddressesUntilReset);
  yield takeEvery(createSavedAddress.request, onCreateSavedAddress);
  yield takeEvery(updateSavedAddress.request, onUpdateSavedAddress);
  yield takeEvery(deleteSavedAddress.request, onDeleteSavedAddress);
  yield takeEvery(markSavedAddressUsed.request, onMarkSavedAddressUsed);
  yield takeEvery([login, logout], onSessionEnd);
}
